import {Button} from '@dropbox/dig-components/buttons';
import {atoms, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import type {
  ComponentProps,
  ComponentType,
  MouseEvent,
  MouseEventHandler,
  Ref,
  SVGAttributes,
} from 'react';

type ButtonProps = ComponentProps<typeof Button>;

export type ToolbarMenuTriggerProps = Pick<
  ButtonProps,
  'id' | 'aria-expanded' | 'aria-controls' | 'aria-haspopup'
> & {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  ref?: Ref<HTMLElement>;
};

type ToolbarOutlineIconButtonProps = {
  icon: ComponentType<SVGAttributes<SVGElement>>;
  count: number;
  ariaLabel: string;
  isCompact: boolean;
  tooltipTitle?: string;
  isHighlighted?: boolean;
  hideCountWhenZero?: boolean;
  ariaPressed?: boolean;
  onClick?: ButtonProps['onClick'];
  triggerProps?: ToolbarMenuTriggerProps;
};

export const ToolbarOutlineIconButton = ({
  icon,
  count,
  ariaLabel,
  isCompact,
  tooltipTitle,
  isHighlighted = false,
  hideCountWhenZero = false,
  ariaPressed,
  onClick,
  triggerProps,
}: ToolbarOutlineIconButtonProps) => {
  const showCount = hideCountWhenZero ? count > 0 : true;
  const {
    onClick: triggerOnClick,
    ref,
    id,
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
    'aria-haspopup': ariaHasPopup,
  } = triggerProps ?? {};

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    triggerOnClick?.(event);
    onClick?.(event);
  };

  return (
    <OptionalTooltip enabled={isCompact} title={tooltipTitle ?? ariaLabel} placement="top">
      <Button
        ref={ref}
        id={id}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        aria-haspopup={ariaHasPopup}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        variant="outline"
        onClick={handleClick}
        withIconStart={!isCompact && showCount ? <UIIcon src={icon} /> : undefined}
        className={
          isHighlighted ? atoms({backgroundColor: 'Opacity Surface - State 1'}) : undefined
        }
        style={isHighlighted ? {borderColor: 'transparent'} : undefined}
      >
        {isCompact ? (
          <Split alignY="center" gap="4">
            <Split.Item>
              <UIIcon src={icon} />
            </Split.Item>
            {showCount ? <Split.Item>{count}</Split.Item> : null}
          </Split>
        ) : showCount ? (
          count
        ) : (
          <UIIcon src={icon} />
        )}
      </Button>
    </OptionalTooltip>
  );
};
